import React, { useState } from "react";
import { CircularProgress, Grid, InputAdornment } from "@mui/material";
import { BankAccount } from "src/store/features/withdrawal.slice";
import { HoldingRes } from "src/store/features/api/generated";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  setError,
  setEmailCode,
  set2FACode,
  setIsEmailCodeSuccess,
} from "src/store/features/withdrawal.slice";
import {
  useCheckWithdrawalEmailCodeMutation,
  useResendWithdrawalEmailCodeMutation,
} from "src/store/features/api/generated";
import { FormInput, InputStyleType } from "../../input/FormInput";
import { truncateEmail } from "src/utils/helpers/EmailTruncate";
import { getSessionItem } from "src/utils/SessionManagement";
import { trackEvent } from "src/Amplitude";
import {
  StyledExplainerTextTypography,
  StyledFormContainer,
  StyledFormHelperText,
  StyledSecurityNoticeTypography,
  StyledStepHeaderTypography,
  FiatWithdrawalVerificationError,
} from "../common/styles/FiatWithdrawalStyled";

interface FiatWithdrawalStageVerificationProps {
  selectedBankAccount: BankAccount | null;
  withdrawalAmount: number;
  isLoading: boolean;
  defaultCurrencyHolding?: HoldingRes;
}

export function FiatWithdrawalStageVerification({
  selectedBankAccount,
  withdrawalAmount,
  isLoading,
  defaultCurrencyHolding,
}: FiatWithdrawalStageVerificationProps) {
  const [emailVerified, setEmailVerified] = useState<Boolean>(false);
  const [sendEmail] = useResendWithdrawalEmailCodeMutation();
  const [resendClicked, setResendClicked] = useState(false);

  const currentEmail = getSessionItem("email");
  const dispatch = useAppDispatch();
  const { emailCode, code2fa, error } = useAppSelector((state) => state.withdrawals);

  const [
    checkEmailCode,
    { isError: checkEmailIsError, isSuccess: checkEmailSuccess, isLoading: emailCheckLoading },
  ] = useCheckWithdrawalEmailCodeMutation();

  const handleEmailChange = async (e: any) => {
    dispatch(setError(""));
    const inputCode = e.target.value;
    dispatch(setEmailCode(inputCode));
    dispatch(setIsEmailCodeSuccess(true));
    if (e.target.value.length === 6) {
      try {
        await checkEmailCode({
          withdrawalVerificationDto: {
            emailCode: e.target.value,
            type: "Fiat",
          },
        })
          .unwrap()
          .then(async () => {
            trackEvent(
              "Successfully validated email code",
              { email: currentEmail },
              "Fiat Withdrawal Modal",
            );
            setEmailVerified(true);
            dispatch(setIsEmailCodeSuccess(true));
          });
      } catch (error: any) {
        trackEvent(
          "Failed to validate email code",
          { email: currentEmail },
          "Fiat Withdrawal Modal",
        );
      }
    }
  };

  const handle2FaChange = async (e: any) => {
    dispatch(setError(""));
    const inputCode = e.target.value;
    if (inputCode?.length <= 6) {
      dispatch(set2FACode(inputCode));
    }
  };

  const handleResendClick = async () => {
    setResendClicked(true);
    if (selectedBankAccount && defaultCurrencyHolding?.currencyConfig.code && withdrawalAmount) {
      await sendEmail({
        sendWithdrawalEmailCodeDto: {
          cryptoAddress: selectedBankAccount.id,
          token: defaultCurrencyHolding.currencyConfig.code,
          amount: withdrawalAmount.toString(),
          type: "Fiat",
        },
      })
        .unwrap()
        .then(() => {
          dispatch(setEmailCode(""));
          dispatch(setError(""));
          trackEvent(
            "Resending Email Verification Code",
            { email: currentEmail },
            "Fiat Withdrawal Modal",
          );
        })
        .catch((error) => {
          trackEvent(
            "Error Trying To Resend Email Verification Code",
            { email: currentEmail },
            "Fiat Withdrawal Modal",
          );
        });
    }

    setTimeout(() => {
      setResendClicked(false);
    }, 10000);
  };

  return (
    <Grid container direction="column" sx={{ paddingBottom: "28px" }}>
      <StyledSecurityNoticeTypography>
        As an added layer of security, we have a 2-step process that you will need to complete to
        proceed with your withdrawal request.
      </StyledSecurityNoticeTypography>

      <StyledFormContainer>
        <StyledStepHeaderTypography>Step 1. Email Verification</StyledStepHeaderTypography>
        <StyledExplainerTextTypography>
          We sent a verification code to your registered email address:{" "}
          <span style={{ fontWeight: 600 }}>{currentEmail && truncateEmail(currentEmail)}</span>
        </StyledExplainerTextTypography>
        <FormInput
          id="withdraw-modal-email-code"
          title="Email Verification Code"
          value={emailCode || ""}
          onChange={handleEmailChange}
          disabled={checkEmailSuccess || emailCheckLoading}
          styleType={checkEmailIsError ? InputStyleType.ERROR : InputStyleType.DEFAULT}
          InputAdornment={
            <InputAdornment position="end">
              {emailCheckLoading ? (
                <CircularProgress size={20} />
              ) : checkEmailSuccess ? (
                <CheckCircleIcon sx={{ color: "#0BEC9F" }} />
              ) : null}
            </InputAdornment>
          }
        />

        {checkEmailIsError && (
          <StyledFormHelperText style={{ color: "#fc4c4c", marginTop: 4 }}>
            Email verification code is invalid, please try again. Resend code{" "}
            <span
              style={{
                textDecoration: "underline",
                cursor: resendClicked ? "not-allowed" : "pointer",
                color: resendClicked ? "#545454" : "#fc4c4c",
                pointerEvents: resendClicked ? "none" : "auto",
              }}
              onClick={resendClicked ? undefined : handleResendClick}>
              here
            </span>
          </StyledFormHelperText>
        )}

        {!checkEmailIsError && !emailVerified && (
          <StyledFormHelperText
            style={{
              color: resendClicked ? "#545454" : "#fc4c4c",
              pointerEvents: resendClicked ? "none" : "auto",
              cursor: resendClicked ? "not-allowed" : "pointer",
            }}>
            If you did not receive it, please click{" "}
            <span
              style={{
                textDecoration: "underline",
                pointerEvents: resendClicked ? "none" : "auto",
                cursor: resendClicked ? "not-allowed" : "pointer",
                color: resendClicked ? "#545454" : "#fc4c4c",
                fontSize: "inherit",
                fontFamily: "inherit",
                fontStyle: "inherit",
              }}
              onClick={handleResendClick}>
              here
            </span>{" "}
            to resend
          </StyledFormHelperText>
        )}

        <Grid
          style={{
            filter: !emailVerified || checkEmailIsError ? "blur(2px)" : "none",
            opacity: !emailVerified || checkEmailIsError ? 0.5 : 1,
            pointerEvents: !emailVerified || checkEmailIsError ? "none" : "auto",
            transition: "filter 0.2s, opacity 0.2s",
            margin: 0,
            padding: 0,
          }}>
          <StyledStepHeaderTypography sx={{ marginTop: "28px" }}>
            Step 2. Two Factor Verification
          </StyledStepHeaderTypography>
          <StyledExplainerTextTypography>
            Enter the 2FA code from your authenticator
          </StyledExplainerTextTypography>
          <FormInput
            id="withdraw-modal-2fa-code"
            title="Two-factor Verification Code"
            value={code2fa || ""}
            onChange={handle2FaChange}
            disabled={!emailVerified || checkEmailIsError}
            styleType={
              error && emailVerified && !checkEmailIsError
                ? InputStyleType.ERROR
                : InputStyleType.DEFAULT
            }
          />

          {emailVerified && !checkEmailIsError && error && (
            <FiatWithdrawalVerificationError>{error}</FiatWithdrawalVerificationError>
          )}
        </Grid>
      </StyledFormContainer>
    </Grid>
  );
}
